import React, { useMemo } from "react";
import type { RedeemRequest } from "types/redeem";
import RedeemItem from "features/redeem/redeem-item/RedeemItem";
import RedeemItemSkeleton from "features/redeem/redeem-item/RedeemItemSkeleton";
import SearchInput from "components/search-input/SearchInput";
import { useSearch } from "components/search-input/useSearch";
import Button from "components/button/Button";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useRedeemLookup } from "features/redeem/useRedeemLookup";
import { useIssuerMerchants } from "pages/issuer-page/useIssuerMerchants";
import { RefreshIconV1 } from "images/image-component";
import "./redeem-list.css";

// The merchant name isn't on the redeem — it's resolved from the merchants map, the same
// lookup RedeemItem renders — so it's folded onto each row to be searchable alongside the id.
interface SearchableRedeem extends RedeemRequest {
  merchantName: string;
}

const REDEEM_SEARCH_KEYS: (keyof SearchableRedeem)[] = ["id", "merchantName"];
// Ids are composite `seq · 10^10 + unix-seconds` decimal strings, so a complete one is
// never shorter than this — below it the operator is still mid-type and the on-chain
// lookup would only answer "not found" for a prefix.
const MIN_REQUEST_ID_LENGTH = 11;
const isFullRequestId = (value: string) =>
  /^\d+$/.test(value) && value.length >= MIN_REQUEST_ID_LENGTH;

interface Props {
  requests: RedeemRequest[];
  loading: boolean;
  processingId: string | null;
  onRefresh: () => void;
  onApprove: (
    requestId: string,
    merchantId: string,
    stripeAmount: number,
  ) => void;
  onReject: (requestId: string) => void;
}

function RedeemList({
  requests,
  loading,
  processingId,
  onRefresh,
  onApprove,
  onReject,
}: Props) {
  const { merchantsMap } = useIssuerMerchants();

  // Memoised so useSearch's own memo isn't invalidated on every render.
  const searchableRequests = useMemo<SearchableRedeem[]>(
    () =>
      requests.map((r) => ({
        ...r,
        merchantName: merchantsMap[r.merchantId] ?? "",
      })),
    [requests, merchantsMap],
  );

  const { query, setQuery, results } = useSearch(
    searchableRequests,
    REDEEM_SEARCH_KEYS,
  );
  const pendingCount = requests.filter((r) => r.status === 0).length;

  const visibleRequests = [...results].sort(
    (a, b) => Number(b.createdAt) - Number(a.createdAt),
  );

  const trimmedQuery = query.trim();

  // Only the recent redeems are loaded up front; a search miss here is our cue to
  // check the chain — but only once a complete request id has been entered.
  const noLocalMatch =
    !loading && trimmedQuery.length > 0 && visibleRequests.length === 0;

  // Hold the network lookup until typing settles, so we don't fire a request
  // (and flash "not found") on every keystroke.
  const debouncedQuery = useDebouncedValue(trimmedQuery, 400);
  const settling = debouncedQuery !== trimmedQuery;
  const lookup = useRedeemLookup(
    debouncedQuery,
    noLocalMatch && isFullRequestId(trimmedQuery) && !settling,
  );

  // While the debounce catches up we're still effectively searching; once it
  // settles, the hook's status drives the outcome (loading/found/miss/error).
  const chainStatus = settling ? "loading" : lookup.status;

  return (
    <section className="rdm-list">
      <div className="rdm-list__header">
        <div className="rdm-list__title-wrap">
          <h3 className="ip-section-title">Redeems</h3>
          {pendingCount > 0 && (
            <span className="rdm-list__count">{pendingCount}</span>
          )}
        </div>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search by request ID or merchant..."
        />
        {/* <button
          className={`rdm-list__refresh-btn${loading ? " rdm-list__refresh-btn--spinning" : ""}`}
          onClick={onRefresh}
          disabled={loading}
          aria-label="Refresh redeem requests"
        >
          <RefreshIconV1 size={16} />
        </button> */}
      </div>

      {loading && requests.length === 0 ? (
        <div className="rdm-list__skeletons">
          {Array.from({ length: 3 }).map((_, i) => (
            <RedeemItemSkeleton key={i} />
          ))}
        </div>
      ) : visibleRequests.length > 0 ? (
        <div className="rdm-list__items">
          {visibleRequests.map((r) => (
            <RedeemItem
              key={r.id}
              request={r}
              onApprove={onApprove}
              onReject={onReject}
              isProcessing={processingId === r.id}
            />
          ))}
        </div>
      ) : trimmedQuery.length === 0 ? (
        <div className="rdm-list__empty">No redeem requests.</div>
      ) : !isFullRequestId(trimmedQuery) ? (
        <div className="rdm-list__empty">
          No matching redeem. Enter the full request ID to look it up on-chain.
        </div>
      ) : chainStatus === "loading" ? (
        <div className="rdm-list__skeletons">
          <RedeemItemSkeleton />
        </div>
      ) : chainStatus === "found" && lookup.request ? (
        <>
          <p className="rdm-list__lookup-note">
            Not in recent redeems — found on-chain.
          </p>
          <div className="rdm-list__items">
            <RedeemItem
              request={lookup.request}
              onApprove={onApprove}
              onReject={onReject}
              isProcessing={processingId === lookup.request.id}
            />
          </div>
        </>
      ) : chainStatus === "not-found" ? (
        <div className="rdm-list__empty">
          No redeem found for “{trimmedQuery}”.
        </div>
      ) : chainStatus === "error" ? (
        <div className="rdm-list__lookup-error">
          <p className="rdm-list__lookup-error-msg">
            Couldn’t look up this redeem right now.
          </p>
          <Button variant="ghost" size="sm" onClick={() => lookup.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        // `idle` — the gate closed under us (e.g. auth dropped). Say nothing rather
        // than claim a lookup failed that never ran.
        <div className="rdm-list__empty">
          No redeem requests match your search.
        </div>
      )}
    </section>
  );
}

export default RedeemList;
